import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Progress } from 'components/ui/progress';
import { Clock, BookOpen, Play, CheckCircle2, Lock, ArrowLeft, Target } from 'lucide-react';

const SkillPathDetail = () => {
  const { id } = useParams();
  const { api } = useAuth();
  const [path, setPath]         = useState(null);
  const [courses, setCourses]   = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading]   = useState(true);

  // ── FIX: envolver en useCallback para incluirla en deps ──
  const fetchData = useCallback(async () => {
    try {
      const [pathRes, coursesRes, progressRes] = await Promise.all([
        api.get(`/skill-paths/${id}`),
        api.get(`/courses?skill_path_id=${id}`),
        api.get('/progress')
      ]);
      setPath(pathRes.data);
      setCourses(coursesRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error fetching skill path:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCourseProgress = (courseId) => progress.find(p => p.course_id === courseId);

  const getOverallProgress = () => {
    if (courses.length === 0) return 0;
    const completedCount = courses.filter(c => getCourseProgress(c.id)?.completed).length;
    return Math.round((completedCount / courses.length) * 100);
  };

  const difficultyColors = {
    beginner:     'bg-green-500/20 text-green-500',
    intermediate: 'bg-yellow-500/20 text-yellow-500',
    advanced:     'bg-red-500/20 text-red-500'
  };

  const difficultyLabels = {
    beginner:     'Principiante',
    intermediate: 'Intermedio',
    advanced:     'Avanzado'
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="h-64 rounded-2xl bg-muted animate-pulse mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!path) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Ruta no encontrada</p>
        <Button asChild className="mt-4"><Link to="/skill-paths">Volver a rutas</Link></Button>
      </div>
    );
  }

  const overallProgress = getOverallProgress();

  return (
    <div className="p-6 space-y-6" data-testid="skill-path-detail">
      <Button variant="ghost" asChild className="mb-2">
        <Link to="/skill-paths"><ArrowLeft className="w-4 h-4 mr-2" />Volver a rutas</Link>
      </Button>

      <div className="relative rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${path.image_url})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="relative p-8 md:p-12">
          <div className="max-w-2xl space-y-4">
            <div className="flex items-center gap-3">
              <Badge className={difficultyColors[path.difficulty]}>{difficultyLabels[path.difficulty]}</Badge>
              <Badge variant="secondary">{path.category}</Badge>
            </div>
            <h1 className="text-3xl md:text-4xl font-light">{path.title}</h1>
            <p className="text-muted-foreground">{path.description}</p>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{path.estimated_hours} horas</span>
              <span className="flex items-center gap-1.5"><BookOpen className="w-4 h-4" />{courses.length} cursos</span>
              <span className="flex items-center gap-1.5"><Target className="w-4 h-4" />{path.skills.length} habilidades</span>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {path.skills.map(skill => <Badge key={skill} variant="outline">{skill}</Badge>)}
            </div>
          </div>
        </div>
      </div>

      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium">Tu Progreso</h3>
              <p className="text-sm text-muted-foreground">
                {courses.filter(c => getCourseProgress(c.id)?.completed).length} de {courses.length} cursos completados
              </p>
            </div>
            <div className="text-3xl font-light text-primary">{overallProgress}%</div>
          </div>
          <Progress value={overallProgress} className="h-3" />
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h2 className="text-xl font-medium">Contenido del Curso</h2>
        <div className="space-y-3">
          {courses.map((course, index) => {
            const courseProgress = getCourseProgress(course.id);
            const isCompleted    = courseProgress?.completed;
            const isInProgress   = courseProgress && !isCompleted;
            const isLocked       = index > 0 && !getCourseProgress(courses[index - 1].id)?.completed;
            return (
              <Card key={course.id}
                className={`glass-card transition-all duration-300 ${isLocked ? 'opacity-60' : 'hover:border-primary/30'}`}
                data-testid={`course-item-${index}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isCompleted ? 'bg-green-500/20' : isInProgress ? 'bg-primary/20' : isLocked ? 'bg-muted' : 'bg-secondary'
                    }`}>
                      {isCompleted ? <CheckCircle2 className="w-6 h-6 text-green-500" />
                        : isLocked ? <Lock className="w-5 h-5 text-muted-foreground" />
                        : <span className="text-lg font-medium">{index + 1}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium truncate">{course.title}</h3>
                        {isInProgress && <Badge variant="secondary" className="text-xs">En progreso</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">{course.description}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{course.duration_minutes} min</span>
                        {isInProgress && <span className="text-primary">{Math.round(courseProgress.progress_percent)}% completado</span>}
                      </div>
                    </div>
                    {!isLocked && (
                      <Button asChild size="sm" className={isCompleted ? 'btn-secondary' : 'btn-primary'}>
                        <Link to={`/course/${course.id}`}>
                          <Play className="w-4 h-4 mr-1" />
                          {isCompleted ? 'Repasar' : isInProgress ? 'Continuar' : 'Empezar'}
                        </Link>
                      </Button>
                    )}
                  </div>
                  {isInProgress && <Progress value={courseProgress.progress_percent} className="h-1 mt-3" />}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SkillPathDetail;